import React, { useState } from "react";
import { Box, Button, FormField, Text, Heading, Tip } from "grommet";
import { Info, Trash, Indicator } from "grommet-icons";
import Select from "react-select";

const ProductsSection = ({
  size,
  formData,
  setFormData,
  products,
  setProducts,
  editIndex,
  setEditIndex,
  productSkills,
  subProductSkills,
  handleDeleteProduct,
}) => {
  // State for selected option
  const [selectedProductOption, setSelectedProductOption] = useState(null);
  const [selectedProductLevel, setSelectedProductLevel] = useState(null);
  // URL path format /add-proficiency/114, extract user ID
  const userIdFromPath = window.location.pathname.split("/")[2];

  const userIdsToFilterCourses = [43, 59]; // only these users can see restricted skills
  const isUserIdInFilterList = userIdsToFilterCourses.includes(
    parseInt(userIdFromPath, 10),
  );

  // These subProduct IDs are restricted: only visible to users in userIdsToFilterCourses
  const restrictedSubProductIds = [128, 129, 130];

  // Prepare options for the dropdown
  const options = productSkills.flatMap((product) =>
    subProductSkills
      .filter((subProduct) => subProduct.product_id === product.id)
      .filter((subProduct) => {
        const isRestricted = restrictedSubProductIds.includes(subProduct.id);

        if (isRestricted) {
          // Restricted skills: show only if user is in the list
          return isUserIdInFilterList;
        }

        // Non-restricted skills: show to everyone
        return true;
      })
      .map((subProduct) => ({
        label: `${product.name} - ${subProduct.skill_name}`,
        value: {
          productName: product.name,
          productId: product.id,
          subProductName: subProduct.skill_name,
          subProductId: subProduct.id,
          is_only_foundation: subProduct.is_only_foundation,
        },
      })),
  );

  const handleAddProduct = () => {
    const { productExperienceYears } = formData;
    if (products.length >= 6) {
      alert("You can only select up to 6 skills.");
      return;
    }
    if (!selectedProductOption) {
      alert("Please select a product and sub-category.");
      return;
    }

    const { productName, productId, subProductName, subProductId } =
      selectedProductOption.value;

    const isProductExists = products.some(
      (product) =>
        product.productName === productName &&
        product.subProductName === subProductName,
    );

    if (isProductExists) {
      alert("This product has already been added.");
      return;
    }

    const newProduct = {
      id: productId,
      productName,
      productLevel: selectedProductLevel?.value,
      productExperienceYears,
      subProductSkillId: subProductId,
      subProductName,
    };

    if (editIndex !== null) {
      const updatedProducts = [...products];
      updatedProducts[editIndex] = newProduct;
      setProducts(updatedProducts);
      setEditIndex(null);
    } else {
      setProducts([...products, newProduct]);
    }

    setFormData({
      ...formData,
      productLevel: "",
      productExperienceYears: "",
    });
    setSelectedProductOption(null);
    setSelectedProductLevel(null);
  };

  // Custom styles for react-select components
  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      width: "100%",
      height: "25px",
      borderColor: state.isFocused ? "#01a982" : provided.borderColor,
      boxShadow: state.isFocused ? "0 0 0 2px #01a982" : provided.boxShadow,
      "&:hover": {
        borderColor: "#01a982",
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? "#01a982" : provided.backgroundColor,
      color: state.isFocused ? "#fff" : provided.color,
      padding: "10px 20px", // Adjusted for better readability
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 5,
    }),
  };
  const baseProductOptions = [
    { label: "Beginner", value: 1 },
    { label: "Intermediate", value: 2 },
    { label: "Advanced", value: 3 },
  ];

  const productOptions =
    selectedProductOption?.value?.is_only_foundation === 1
      ? baseProductOptions.map((option) =>
        option.value === 1 ? option : { ...option, isDisabled: true },
      )
      : baseProductOptions;

  return (
    <Box>
      {/* Header and Two Columns for Intro and Steps */}

      {/* Wrap both sections in a parent row container */}
      <Box
        direction="row"
        gap="large"
        justify="between"
        width="xlarge"
        margin={{ vertical: "medium" }}
      >
        {/* Left Box with introductory text */}
        <Box
          background="light-2" // Light background to differentiate the section
          pad="medium" // Padding for a more spaced-out appearance
          round="small" // Rounded edges for better aesthetics
          width="large" // Increased width for better spacing
        >
          <Text size="medium" color="dark-3">
            The HPE Digital Skill Advisor allows you to assess your team’s
            proficiency in various technical solutions and topics. By answering
            a few questions, it will provide insights into your team’s current
            skill levels and recommend personalized learning pathways to support
            their growth.
          </Text>
          <Text
            size="medium"
            color="dark-3"
            alignSelf="left"
            margin={{ top: "small" }}
          >
            To begin, select at least 1 and at most 6 skills that you’d like
            your team to be assessed on. Once you’re ready, click{" "}
            <b>‘Proceed to skills check’</b> to start the process.
          </Text>
          <Box
            direction="row"
            gap="small"
            align="center"
            margin={{ top: "small" }}
          >
            <Indicator color="brand" />
            <Text size="medium" color="dark-1">
              You can reassess your team at any time to track progress and
              refine their skill development plans.
            </Text>
          </Box>
        </Box>

        {/* Right Box with step-by-step instructions */}
        <Box
          background="light-2" // Light background to differentiate the section
          pad="medium" // Padding for a more spaced-out appearance
          round="small" // Rounded edges for better aesthetics
          width="large" // Increased width for better spacing
        >
          {/* Step-by-step instructions with icons */}
          <Box direction="row" gap="small" align="center">
            <Info color="brand" />
            <Text size="medium" color="dark-1">
              <strong>Step 1:</strong> Identify and select the skill areas
              relevant to your team’s development.
            </Text>
          </Box>

          <Box
            direction="row"
            gap="small"
            align="center"
            margin={{ top: "small" }}
          >
            <Box direction="row" align="start" gap="xsmall">
              <Info color="brand" />
              <Text size="medium" color="dark-1">
                <strong>Step 2:</strong> Specify the level of proficiency for
                each selected skill based on your team’s current expertise—
                <br />
                {"  "}
                <strong>Beginner:</strong> Select this if your team has less
                than 2 years of experience in the skill.
                <br />
                {"  "}
                <strong>Intermediate:</strong> Select this if your team has 2 to
                5 years of experience in the skill.
                <br />
                {"  "}
                <strong>Advanced:</strong> Select this if your team has over 5
                years of experience and demonstrates deep expertise in the
                skill.
              </Text>
            </Box>
          </Box>

          <Box
            direction="row"
            gap="small"
            align="center"
            margin={{ top: "small" }}
          >
            <Info color="brand" />
            <Text size="medium" color="dark-1">
              <strong>Step 3:</strong> After selecting skills and proficiency
              levels, click <strong>‘Add’</strong>. You can repeat this process
              to include up to <strong>6 skills</strong> for assessment.
            </Text>
          </Box>
        </Box>
      </Box>

      {/* Product Based Skills Section */}
      <Box direction="row" align="start" margin={{ top: "medium" }}>
        <Heading level="3" size="large">
          Product based skills
        </Heading>
        <Box pad={{ horizontal: "small" }}>
          <Tip content="Select product based skills">
            <Info color="brand" size="small" />
          </Tip>
        </Box>
      </Box>

      <Box
        border={{ color: "brand", size: "small" }}
        margin={{ bottom: "small" }}
      />

      {/* Form Fields */}
      <Box direction="column" gap="small">
        <Box
          direction={size === "small" ? "column" : "row"}
          gap="medium"
          align="center"
          width="xlarge"
        >
          {/* Single-select Dropdown with Search */}
          <FormField name="productSubCategory" style={{ flex: 1 }}>
            <Select
              options={options}
              value={selectedProductOption}
              placeholder="Select skill"
              onChange={(option) => {
                setSelectedProductOption(option);
                setSelectedProductLevel(null);
              }}
              isSearchable
              styles={customSelectStyles}
            />
          </FormField>

          {/* Level Select */}
          <FormField name="productLevel" style={{ flex: 1 }}>
            <Select
              options={productOptions}
              value={selectedProductLevel}
              placeholder="Select your skill level"
              onChange={(option) => setSelectedProductLevel(option)}
              isSearchable={false}
              styles={customSelectStyles}
            />
          </FormField>

          {/* Add/Update Button */}
          <Button
            primary
            label={"Add"}
            onClick={handleAddProduct}
            disabled={!selectedProductOption || !selectedProductLevel}
            style={{ width: "100px" }}
          />
        </Box>

        {/* Display Added Products */}
        <Box margin={{ top: "small" }}>
          <Text size="medium" color="grey">
            Selected skill(s):
          </Text>
          {products.map((product, index) => (
            <Box
              key={index}
              direction={size === "small" ? "column" : "row"}
              justify="between"
              align="center"
              background="light-2"
              pad={{ vertical: "small", horizontal: "medium" }}
              round="small"
              margin={{ vertical: "xsmall" }}
            >
              <Text>
                {`${product.productName} - ${product.subProductName}`}
                {product.productLevel &&
                  ` - ${product.productLevel === 1
                    ? "Beginner"
                    : product.productLevel === 2
                      ? "Intermediate"
                      : product.productLevel === 3
                        ? "Advanced"
                        : "Unknown"
                  }`}
              </Text>
              <Box direction="row" gap="small">
                <Button
                  icon={<Trash />}
                  onClick={() => handleDeleteProduct(index)}
                />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default ProductsSection;
